<template>
    <div :style="{ height: height + 'px', width: width + 'px' }">
        <Bar :data="chartData" :options="computedOptions" :height="height" :width="width" />
    </div>
</template>

<script>
    import { Bar } from 'vue-chartjs'

    export default {
        name: 'BarChartStacked',
        components: {
            Bar: Bar
        },
        props: {
            chartData: {
                type: Object,
                required: true
            },
            xAxesLabelString: {
                type: String,
                required: true
            },
            yAxesLabelString: {
                type: String,
                required: true
            },
            yAxesLabelStringTwo: {
                type: String,
                default: ''
            },
            height: {
                type: Number,
                default: 350
            },
            width: {
                type: Number,
                default: 350
            },
            integerOnly: {
                type: Boolean,
                default: false
            }
        },
        computed: {
            computedOptions() {
                const yAxesList = this.yAxesLabelString.split('<br>')
                const dataset = this.chartData.datasets?.[0] || {}
                const isPercent = dataset.isPercentValue
                const maxValue = dataset.maxValue || undefined
                const maxValueRight = this.chartData.datasets?.[1]?.maxValue || undefined
                // precision: 0 unterdrückt 0,5/1,5-Ticks bei "Anzahl …"-Kennzahlen.
                // Auf der rechten Achse (Counts) immer ganzzahlig, auf der linken Achse
                // nur wenn nicht Prozent.
                const integerTicksLeft = this.integerOnly && !isPercent
                const integerTicksRight = this.integerOnly

                const yAxisLeft = {
                    type: 'linear',
                    position: 'left',
                    stacked: true,
                    ticks: {
                        ...(integerTicksLeft ? { precision: 0 } : {}),
                        callback: (value) => {
                            if (isPercent) return `${(value * 100).toFixed(0)} %`
                            return value.toString().replace('.', ',')
                        },
                        font: {
                            family: "'Source Sans Pro'",
                            size: 13
                        }
                    },
                    title: {
                        display: true,
                        text: yAxesList.length === 2 ? yAxesList[1] : yAxesList[0],
                        font: {
                            family: "'Source Sans Pro'",
                            size: 13
                        }
                    },
                    suggestedMax: maxValue
                }

                const yAxisRight = {
                    type: 'linear',
                    position: 'right',
                    stacked: true,
                    grid: {
                        display: false
                    },
                    ticks: {
                        ...(integerTicksRight ? { precision: 0 } : {}),
                        font: {
                            family: "'Source Sans Pro'",
                            size: 13
                        }
                    },
                    title: {
                        display: true,
                        text: this.yAxesLabelStringTwo,
                        font: {
                            family: "'Source Sans Pro'",
                            size: 13
                        }
                    },
                    suggestedMax: maxValueRight
                }

                const extraYAxis =
                    yAxesList.length === 2
                        ? {
                              type: 'linear',
                              position: 'left',
                              display: true,
                              grid: {
                                  display: false
                              },
                              ticks: {
                                  display: false
                              },
                              title: {
                                  display: true,
                                  text: yAxesList[0],
                                  font: {
                                      family: "'Source Sans Pro'",
                                      size: 13
                                  }
                              }
                          }
                        : undefined

                return {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: true,
                            position: 'top',
                            labels: {
                                font: {
                                    family: "'Source Sans Pro'",
                                    size: 13
                                },
                                boxWidth: 8,
                                usePointStyle: true
                            }
                        },
                        tooltip: {
                            callbacks: {
                                title: (tooltipItems) => {
                                    return tooltipItems[0]?.dataset?.data[tooltipItems[0].dataIndex]?.x
                                },
                                label: (tooltipItem) => {
                                    let label = tooltipItem.dataset.label || ''
                                    if (label) label += ': '
                                    const y = tooltipItem.raw?.y ?? tooltipItem.parsed.y
                                    return tooltipItem.dataset.isPercentValue
                                        ? `${(y * 100).toFixed(0)} %`
                                        : y.toString().replace('.', ',')
                                }
                            }
                        }
                    },
                    scales: {
                        x: {
                            stacked: true,
                            title: {
                                display: true,
                                text: this.xAxesLabelString,
                                font: {
                                    family: "'Source Sans Pro'",
                                    size: 13
                                }
                            },
                            ticks: {
                                font: {
                                    family: "'Source Sans Pro'",
                                    size: 13
                                }
                            }
                        },
                        y: yAxisLeft,
                        y1: yAxisRight,
                        ...(extraYAxis ? { y2: extraYAxis } : {})
                    }
                }
            }
        }
    }
</script>

<style scoped></style>
